import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import {
  LogIn,
  LayoutDashboard,
  UserCircle,
  UserPlus,
  Menu as MenuIcon,
  X as XIcon,
} from 'lucide-react';
import { useAuth } from '@/features/auth/useAuth';
import { BRAND, FOOTER, SOCIALS } from '@/data/marketing/content';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import CursorDot from '@/components/CursorDot';
import ChatWidget from '@/features/chatbot/ChatWidget';

/**
 * Layout du site vitrine public (route `/` et `/menu`).
 * - Navbar sticky avec logo + liens + bouton compte/dashboard
 * - Scroll progress bar dorée en haut
 * - Underline animée sous le NavLink actif (layoutId)
 * - Outlet pour le contenu de la page
 * - Footer marketing
 *
 * Différent de AppLayout (qui est pour l'app de gestion protégée).
 */

const NAV_ITEMS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Accueil', end: true },
  { to: '/bouffe', label: 'Bouffe' },
  { to: '/vins', label: 'Vins' },
  { to: '/cocktails', label: 'Cocktails' },
  { to: '/futs', label: 'Fûts' },
  { to: '/degustation', label: 'Dégustation' },
  { to: '/evenements', label: 'Événements' },
  { to: '/emplois', label: 'Emplois' },
  { to: '/a-propos', label: 'À propos' },
  { to: '/reserver', label: 'Réserver' },
];

export default function PublicLayout() {
  const { user, isCustomer, profile } = useAuth();
  const { pathname, hash } = useLocation();
  const { scrollYProgress } = useScroll();
  const [mobileOpen, setMobileOpen] = useState(false);
  // isStaff = a un user_profile staff (peut accéder au dashboard)
  const isStaff = !!profile;

  // Scroll en haut au changement de page (sauf hash)
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [pathname, hash]);

  // Ferme le menu mobile au changement de route + lock du scroll body
  // pendant qu'il est ouvert (sinon on scroll la page derrière le panel).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setMobileOpen(false);
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = previous;
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E8E0D6] flex flex-col">
      {/* Skip-link a11y : invisible jusqu'au focus clavier (Tab) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-[#D4A574] focus:text-[#0A0A0A] focus:px-4 focus:py-2 focus:rounded-md focus:font-semibold focus:shadow-lg"
      >
        Aller au contenu
      </a>

      {/* ───────── Curseur custom doré (pointer:fine + non-reduced-motion) ───────── */}
      <CursorDot />

      {/* ───────── Scroll progress bar (dorée, 2px en haut) ───────── */}
      <motion.div
        className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-[#D4A574] origin-left pointer-events-none"
        style={{ scaleX: scrollYProgress }}
        aria-hidden="true"
      />

      {/* ───────── Navbar sticky ───────── */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-5 lg:px-8 py-4">
          {/* Logo + nom */}
          <Link to="/" className="group flex items-center gap-2.5 min-w-0">
            <Logo size="h-9 md:h-11 shrink-0" />
            <span className="font-display text-lg md:text-2xl font-bold text-[#D4A574] leading-none transition-colors group-hover:text-[#E8C49A] truncate">
              {BRAND.name}
            </span>
          </Link>

          {/* ── Nav desktop (lg+) ── */}
          <nav className="hidden lg:flex items-center justify-end gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'relative inline-block px-3 py-2 text-sm tracking-wide transition-colors rounded-md whitespace-nowrap',
                    isActive
                      ? 'text-[#D4A574]'
                      : 'text-neutral-400 hover:text-[#E8E0D6]'
                  )
                }
              >
                {({ isActive }) => (
                  <span className="relative inline-block">
                    {item.label}
                    {isActive && (
                      <motion.span
                        layoutId="public-nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-px bg-[#D4A574]"
                        transition={{
                          type: 'spring',
                          stiffness: 380,
                          damping: 30,
                        }}
                      />
                    )}
                  </span>
                )}
              </NavLink>
            ))}

            <HeaderCTA
              isStaff={isStaff}
              isCustomer={isCustomer}
              hasUser={!!user}
            />
          </nav>

          {/* ── Bouton hamburger mobile (<lg) ── */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#2A2A2A] bg-[#141414] text-[#D4A574] hover:bg-[#1A1A1A] hover:border-[#D4A574]/40 transition-colors shrink-0"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* ───────── Drawer mobile (slide-in droite) ───────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
              aria-hidden="true"
            />
            {/* Panel */}
            <motion.aside
              key="panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
              className="lg:hidden fixed top-0 right-0 bottom-0 z-[70] w-[85%] max-w-sm bg-[#0F0F0F] border-l border-[#1F1F1F] flex flex-col"
              role="dialog"
              aria-label="Menu de navigation"
            >
              {/* Header drawer */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F1F1F]">
                <div className="flex items-center gap-2.5">
                  <Logo size="h-9" />
                  <span className="font-display text-lg font-bold text-[#D4A574] leading-none">
                    {BRAND.name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Fermer le menu"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md text-neutral-400 hover:text-[#D4A574] hover:bg-[#1A1A1A] transition-colors"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto px-3 py-4">
                <ul className="space-y-1">
                  {NAV_ITEMS.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'block px-4 py-3 rounded-lg text-base transition-colors',
                            isActive
                              ? 'bg-[#D4A574]/15 text-[#D4A574] font-semibold border border-[#D4A574]/30'
                              : 'text-neutral-300 hover:bg-[#1A1A1A] hover:text-[#E8E0D6] border border-transparent'
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* CTA bas */}
              <div
                className="border-t border-[#1F1F1F] p-4 flex flex-col gap-2"
                onClick={() => setMobileOpen(false)}
              >
                <HeaderCTA
                  isStaff={isStaff}
                  isCustomer={isCustomer}
                  hasUser={!!user}
                  block
                />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ───────── Contenu page (fade-in seul à l'entrée — pas d'exit pour
                   ne pas confuser Suspense des routes lazy) ───────── */}
      <main id="main-content" className="flex-1">
        <motion.div
          key={pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <Outlet />
        </motion.div>
      </main>

      {/* ───────── Chatbot widget (bulle flottante bas-droite) ───────── */}
      <ChatWidget />

      {/* ───────── Footer marketing ───────── */}
      <footer className="border-t border-[#1F1F1F] bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3">
              <Logo size="h-12" />
              <div>
                <h3 className="font-display text-2xl text-[#D4A574] leading-tight">
                  {BRAND.name}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
                  L'univers de la dégustation
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
              {FOOTER.tagline}
            </p>
            <p className="mt-3 text-xs text-neutral-300">
              {BRAND.address}
            </p>
            <div className="mt-3 flex flex-col gap-1.5">
              <a
                href={`tel:${BRAND.phone.replace(/[^0-9+]/g, '')}`}
                className="inline-block text-xs text-neutral-300 hover:text-[#D4A574] transition-colors tabular-nums w-fit"
              >
                {BRAND.phone}
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                className="inline-block text-xs text-neutral-300 hover:text-[#D4A574] transition-colors w-fit"
              >
                {BRAND.email}
              </a>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 mb-3">
              Naviguer
            </div>
            <ul className="space-y-2">
              {FOOTER.links.map((l) => (
                <li key={l.href}>
                  {l.href.startsWith('mailto:') ? (
                    <a
                      href={l.href}
                      className="text-sm text-neutral-400 hover:text-[#D4A574] transition-colors"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      className="text-sm text-neutral-400 hover:text-[#D4A574] transition-colors"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 mb-3">
              Suivez-nous
            </div>
            <div className="flex gap-2 mb-5">
              <a
                href={SOCIALS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-[#2A2A2A] bg-[#0A0A0A] text-neutral-400 hover:text-[#D4A574] hover:border-[#D4A574]/40 hover:bg-[#D4A574]/5 transition-all"
                aria-label="Bar L'Alambic sur Facebook"
              >
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-[#2A2A2A] bg-[#0A0A0A] text-neutral-400 hover:text-[#D4A574] hover:border-[#D4A574]/40 hover:bg-[#D4A574]/5 transition-all"
                aria-label="Bar L'Alambic sur Instagram"
              >
                <InstagramIcon className="h-4 w-4" />
              </a>
            </div>
            <p className="text-xs text-neutral-400 leading-relaxed">
              {FOOTER.legal}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CTA droit (Dashboard / Mon compte / Crée ton compte) — partagé
// entre la navbar desktop et le drawer mobile. La prop `block`
// passe le bouton en pleine largeur pour le drawer mobile.
// ─────────────────────────────────────────────────────────────

function HeaderCTA({
  isStaff,
  isCustomer,
  hasUser,
  block = false,
}: {
  isStaff: boolean;
  isCustomer: boolean;
  hasUser: boolean;
  block?: boolean;
}) {
  const baseBtn = cn(
    'inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all',
    block ? 'w-full justify-center' : ''
  );

  if (isStaff) {
    return (
      <Link
        to="/shifts"
        className={cn(
          baseBtn,
          'border border-[#D4A574]/40 bg-[#D4A574]/10 text-[#D4A574] hover:bg-[#D4A574]/20 hover:border-[#D4A574]/60',
          !block && 'ml-2'
        )}
      >
        <LayoutDashboard className="h-3.5 w-3.5" />
        Dashboard
      </Link>
    );
  }
  if (isCustomer || hasUser) {
    return (
      <Link
        to="/mon-compte"
        className={cn(
          baseBtn,
          'border border-[#D4A574]/40 bg-[#D4A574]/10 text-[#D4A574] hover:bg-[#D4A574]/20 hover:border-[#D4A574]/60',
          !block && 'ml-2'
        )}
      >
        <UserCircle className="h-3.5 w-3.5" />
        Mon compte
      </Link>
    );
  }
  return (
    <>
      <Link
        to="/compte/connexion"
        className={cn(
          baseBtn,
          'text-neutral-400 hover:text-[#E8E0D6]',
          block ? '' : 'hidden sm:inline-flex'
        )}
      >
        <LogIn className="h-3.5 w-3.5" />
        Connexion
      </Link>
      <Link
        to="/compte/inscription"
        className={cn(
          baseBtn,
          'border border-[#D4A574]/40 bg-transparent text-[#D4A574] hover:bg-[#D4A574]/10 hover:border-[#D4A574]/60',
          !block && 'ml-1'
        )}
      >
        <UserPlus className="h-3.5 w-3.5" />
        Crée ton compte
      </Link>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Icônes brand (Facebook, Instagram) — SVG inline
// Lucide a retiré les logos de marque, donc on les inline.
// ─────────────────────────────────────────────────────────────

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.13 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.81 8.44-4.94 8.44-9.94z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}
